import { useRef, useState } from 'react';
import { resolveCoverUrl } from '../../../types/manageBooks';
import { useTranslation } from '../../../i18n/useTranslation';

interface Props {
  currentUrl: string | null | undefined;   // raw blob path OR wrapped URL
  onUpload: (file: File) => Promise<void>;
  busy: boolean;
}

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 2 * 1024 * 1024;

export function BookCoverUploader({ currentUrl, onUpload, busy }: Props) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const currentDisplay = resolveCoverUrl(currentUrl ?? null);

  const handlePick = (file: File) => {
    setError(null);
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError(t('App.Admin.Books.BookCoverUploader.ErrorInvalidType'));
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(t('App.Admin.Books.BookCoverUploader.ErrorTooLarge'));
      return;
    }
    const blobUrl = URL.createObjectURL(file);
    setPreviewUrl(blobUrl);
    onUpload(file)
      .catch(err => setError(err instanceof Error ? err.message : t('App.Admin.Books.BookCoverUploader.UploadFailed')))
      .finally(() => {
        URL.revokeObjectURL(blobUrl);
        setPreviewUrl(null);
      });
  };

  return (
    <div>
      <div className="flex items-start gap-4">
        <img
          src={previewUrl ?? currentDisplay ?? ''}
          alt=""
          className="w-32 h-48 object-cover bg-neutral-200 border"
        />
        <div>
          <input
            ref={inputRef}
            type="file"
            accept={ALLOWED_TYPES.join(',')}
            className="hidden"
            onChange={e => {
              const f = e.target.files?.[0];
              if (f) handlePick(f);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="px-4 py-2 border rounded min-h-[44px] disabled:opacity-50"
          >
            {busy ? t('App.Admin.Books.BookCoverUploader.Uploading') : (currentDisplay ? t('App.Admin.Books.BookCoverUploader.ReplaceCover') : t('App.Admin.Books.BookCoverUploader.UploadCover'))}
          </button>
          <p className="text-xs text-neutral-500 mt-2">
            {t('App.Admin.Books.BookCoverUploader.AllowedFormats')}
          </p>
          {error && <p role="alert" className="text-sm text-red-600 mt-1">{error}</p>}
        </div>
      </div>
    </div>
  );
}
